/**
 * Process Manager
 * 
 * This utility manages multi-step processes that may be interrupted (like user registration or invitation flows).
 * It uses session storage to track the state of active processes, allowing them to be resumed after interruptions
 * such as page reloads, redirects, or when a user needs to complete another process first.
 * 
 * Key concepts:
 * - Active Process: The current process being tracked (only one can be active at a time)
 * - Process State: The current state/step of the active process
 * - Process Interruption: When a process is temporarily paused (e.g., for login)
 * 
 * Example flow:
 * 1. User starts an invitation acceptance process (becomes the active process)
 * 2. During invitation, user clicks "Forgot Password" 
 * 3. Invitation acceptance process is marked as interrupted
 * 4.  After password reset completes, user can resume the invitation process
 * 
 * Usage:
 * 1. Start a process: processManager.startProcess(PROCESS_NAME, initialState, params)
 * 2. Update state: processManager.updateProcessState({ currentStep: 'step2' })
 * 3. Mark as interrupted: processManager.markAsInterrupted(INTERRUPTION_TYPE)
 * 4. Check for interrupted process: processManager.getInterruptedProcessInfo()
 * 5. Resume or clear process: processManager.resumeProcess() or processManager.clearProcess()
 * 
 * To add new process types or steps:
 * 1. Update process_constants.js to add new PROCESS_NAMES or step constants
 * 2. Add corresponding message handlers in getPromptMessage() if needed
 * 3. Use the constants in your application code via processManager.PROCESS_NAMES
 * 
 * Note: When checking for an active process, use hasActiveProcess() and getActiveProcess()
 * When checking for an interrupted process, use getInterruptedProcessInfo()
 * 
 * Future Enhancement:
 * This manager could be extended to support process stacking with LIFO (Last In, First Out) behavior,
 * allowing nested processes where a new process can interrupt the current one, and upon completion,
 * the previous process would automatically resume. This would require:
 * - A process stack in session storage
 * - pushProcess() and popProcess() methods to manage the stack
 * - Modifications to handle state transitions between stacked processes
 */

define(function (require) {
  const sessionUtilities = require('./session_utilities');
  const processConstants = require('./process_constants');
  const general_utilities = require('civicsUtils/general_utilities');

  return {
    // Constants for external use
    INVITE_STEPS: processConstants.INVITE_STEPS,
    PROCESS_NAMES: processConstants.PROCESS_NAMES,
    INTERRUPTION_TYPES: processConstants.INTERRUPTION_TYPES,
    NAMESPACE: processConstants.NAMESPACE,
    // Start tracking a process flow
    startProcess: function(processName, state = {}, params = {}) {
      sessionUtilities.saveSessionData(processName, processConstants.ACTIVE_PROCESS, this.PROCESS_NAMESPACE);
      sessionUtilities.saveSessionData(state, processConstants.PROCESS_STATE, this.PROCESS_NAMESPACE);
      sessionUtilities.saveSessionData(params, processConstants.PROCESS_PARAMS, this.PROCESS_NAMESPACE);
      if (params.returnURL) {
        sessionUtilities.saveSessionData(params.returnURL, processConstants.RETURN_URL, this.PROCESS_NAMESPACE);
      }
    },
    
    // Update the state of the current process
    updateProcessState: function(state) {
      const currentState = this.getProcessState() || {};
      sessionUtilities.saveSessionData({...currentState, ...state}, processConstants.PROCESS_STATE, this.PROCESS_NAMESPACE);
    },
    
    // Get the name of the active process
    getActiveProcess: function() {
      return sessionUtilities.getSessionData(processConstants.ACTIVE_PROCESS, this.PROCESS_NAMESPACE);
    },
    
    // Get the current state of the active process
    getProcessState: function() {
      return sessionUtilities.getSessionData(processConstants.PROCESS_STATE, this.PROCESS_NAMESPACE);
    },
    
    // Get the parameters of the active process
    getProcessParams: function() {
      return sessionUtilities.getSessionData(processConstants.PROCESS_PARAMS, this.PROCESS_NAMESPACE);
    },
    
    // Check if there is an active process
    hasActiveProcess: function() {
      return !!this.getActiveProcess();
    },

    // Return to the active process using a prompt or auto-redirect. 
    resumeProcess: function(showPrompt = false) {
      const interruptedProcess = this.getInterruptedProcessInfo();

      if (interruptedProcess) {
        if (interruptedProcess.autoReturn && !showPrompt) {
            window.location.replace(this.getReturnURL());
            sessionUtilities.clearSessionData(processConstants.PROCESS_INTERRUPTED, this.PROCESS_NAMESPACE);
        } else if (showPrompt === true) {
            general_utilities.civicsToastPop(this.getPromptMessage(interruptedProcess.processName, this.getReturnURL()), processConstants.PROMPT_TYPE, 10000000);
        }
      }
    },

    //Get the prompt message for the current active process.
    getPromptMessage: function(activeProcessName, targetPage = '') {
      let promptMessage = '';
      let linkName = ''
      switch (activeProcessName) {
        case this.PROCESS_NAMES.INVITATION:
          promptMessage = i18n.t('CIVICS.INVITE.RESUME_INVITE_PROMPT_MSG');
          linkName = i18n.t('CIVICS.INVITE.RESUME_INVITE_PROMPT_LINK');
          break;
        default:
          promptMessage = processConstants.DEFAULT_PROMPT_MSG;
          break;
      }

      if (targetPage.length > 0) {
        promptMessage = promptMessage + ' ' + '<a href="'+ targetPage 
        + '" class="link-primary activeProcessLink" style="display:inline">' + linkName + '</a>';
      }

      return promptMessage;
    },

    // Mark process as interrupted with autoReturn option
    markAsInterrupted: function(interruptType = this.INTERRUPTION_TYPES.FORGOTPASSWORD, autoReturn = false) {
      if (this.hasActiveProcess()) {
        sessionUtilities.saveSessionData(interruptType, processConstants.PROCESS_INTERRUPTED, this.PROCESS_NAMESPACE);
        sessionUtilities.saveSessionData(autoReturn, processConstants.AUTO_RETURN, this.PROCESS_NAMESPACE);
      }
    },
    
    // Check if process was interrupted
    wasInterrupted: function() {
      return !!sessionUtilities.getSessionData(processConstants.PROCESS_INTERRUPTED, this.PROCESS_NAMESPACE);
    },


    
    // Get interrupted process info for resume prompt
    getInterruptedProcessInfo: function() {
      if (!this.hasActiveProcess() || !this.wasInterrupted()) {
        return null;
      }
      
      const processName = this.getActiveProcess();
      const state = this.getProcessState();
      const interruptType = sessionUtilities.getSessionData(processConstants.PROCESS_INTERRUPTED, this.PROCESS_NAMESPACE);
      const autoReturn = sessionUtilities.getSessionData(processConstants.AUTO_RETURN, this.PROCESS_NAMESPACE);

      let retMessage = '';
      let retLinkName = '';
      switch (processName) {
        case this.PROCESS_NAMES.INVITATION:
          retMessage = i18n.t('CIVICS.INVITE.RESUME_INVITE_PROMPT_MSG');
          retLinkName = i18n.t('CIVICS.INVITE.RESUME_INVITE_PROMPT_LINK');
          break;
        default:
          break;
      }

      return {
        processName,
        currentStep: state?.currentStep,
        interruptType,
        canResume: true,
        autoReturn: autoReturn,
        message: retMessage,
        targetLinkName: retLinkName,
        returnUrl: this.getReturnURL()
      };
    },

    clearProcessInterruption: function() {
      sessionUtilities.clearSessionData(processConstants.PROCESS_INTERRUPTED, this.PROCESS_NAMESPACE);
      sessionUtilities.clearSessionData(processConstants.AUTO_RETURN, this.PROCESS_NAMESPACE);
    },
    
    // Get return URL for resuming process
    getReturnURL: function() {
      return sessionUtilities.getSessionData(processConstants.RETURN_URL, this.PROCESS_NAMESPACE);
    },
    
    // Set current workflow step
    setCurrentStep: function(stepName) {
      this.updateProcessState({ currentStep: stepName });
    },
    
    // Clear all process data
    clearProcess: function() {
      sessionUtilities.clearSessionData(processConstants.ACTIVE_PROCESS, this.PROCESS_NAMESPACE);
      sessionUtilities.clearSessionData(processConstants.PROCESS_STATE, this.PROCESS_NAMESPACE);
      sessionUtilities.clearSessionData(processConstants.PROCESS_PARAMS, this.PROCESS_NAMESPACE);
      sessionUtilities.clearSessionData(processConstants.RETURN_URL, this.PROCESS_NAMESPACE);
      sessionUtilities.clearSessionData(processConstants.PROCESS_INTERRUPTED, this.PROCESS_NAMESPACE);
    }
  };
});
